import React, { FC, useState, ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';

import Visibility from '@mui/icons-material/VisibilityOutlined';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';
import {
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Button,
  Link,
} from '@mui/material';
import InputField from 'shared/components/input/input.component';

import useLogin from '../Login.service';
import MaskedPhoneNumber from 'components/MaskedPhoneNumber/MaskedPhoneNumber';
import LoadingButton from 'components/loading-button/loading-button.component';
import { STRINGS } from 'utils/constants';
import zoomSdk from '@zoom/appssdk';
import FccDisclaimer from './fcc-disclaimer.component';
import ErrorMessage from 'shared/components/error-message/error-message.component';
import { ERROR_TEXT } from 'utils/styles/colors';

interface Inputs {
  username: string;
  password: string;
}

const LoginForm: FC = () => {
  const { login, loginError, loading, userInterfaceModeError } = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();

  const handleOnChange = ({ target }: ChangeEvent<HTMLInputElement>) =>
    setPhoneNumber(target.value);

  const handleOnBlur = () => setValue('username', phoneNumber);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const urls = {
    password_reset: 'https://www.sorensonvrs.com/secured_pword_reset',
    mailto: `${process.env.REACT_APP_API_BASE_URL}/zoomapp/mailto?to=SICustomerSupport@sorenson.com`,
  };

  const handleOpenUrl = (url: string) => {
    zoomSdk.openUrl({ url });
  };

  return (
    <Box>
      <form
        onSubmit={handleSubmit(login)}
        autoComplete={rememberPassword ? 'on' : 'off'}
        id='login-form'
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
          id='login-form-container'
        >
          <InputField
            id='phone-number-input'
            label='Sorenson Phone Number'
            placeholder='Sorenson Phone Number'
            error={!!errors.username || loginError}
            InputLabelProps={{ shrink: true, variant: 'standard' }}
            InputProps={{
              inputComponent: MaskedPhoneNumber as any,
            }}
            helperText={errors?.username?.message}
            {...register('username', {
              required: 'Sorenson Phone Number is required!',
              onChange: handleOnChange,
              onBlur: handleOnBlur,
            })}
          />

          <InputField
            id='password-input'
            label='Sorenson Password'
            placeholder='Sorenson Password'
            error={!!errors.password || loginError}
            helperText={errors?.password?.message}
            type={showPassword ? 'text' : 'password'}
            autoComplete={rememberPassword ? 'on' : 'new-password'}
            InputLabelProps={{ shrink: true, variant: 'standard' }}
            sx={{ marginBottom: '24px' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={handleClickShowPassword}
                    edge='end'
                  >
                    {showPassword ? (
                      <Visibility sx={{ color: 'text.disabled' }} />
                    ) : (
                      <VisibilityOff sx={{ color: 'text.disabled' }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...register('password', {
              required: 'Sorenson Password is required!',
            })}
          />
          {loginError && !userInterfaceModeError && (
            <Typography sx={{ color: 'error.main', fontSize: '14px' }}>
              {STRINGS.ERROR_STRINGS.SIGN_IN_FAILURE}
            </Typography>
          )}
          <LoadingButton
            type='submit'
            loading={loading}
            style={{ marginTop: '24px' }}
            id='login-form-submit-button'
          >
            <Typography sx={{ fontSize: '14px', fontWeight: '700' }}>
              Continue
            </Typography>
          </LoadingButton>

          {userInterfaceModeError !== '' && (
            <ErrorMessage>
              <span
                style={{
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  lineHeight: '20px',
                  color: ERROR_TEXT,
                }}
              >
                {userInterfaceModeError}
                <Link
                  onClick={() => handleOpenUrl(urls.mailto)}
                  style={{
                    textDecoration: 'none',
                    color: ERROR_TEXT,
                    borderBottom: `1px solid ${ERROR_TEXT}`,
                    cursor: 'pointer',
                  }}
                >
                  sign up with Sorenson Express
                </Link>
                ?
              </span>
            </ErrorMessage>
          )}

          <Box
            id='login-form-forgot-password-container'
            sx={{ margin: '16px 0', display: 'flex', justifyContent: 'center' }}
          >
            <Button
              sx={{
                color: 'text.primary',
                fontSize: '14px',
                textAlign: 'center',
                fontWeight: '700',
                textTransform: 'none',
              }}
              onClick={() => handleOpenUrl(urls.password_reset)}
              id='login-form-forgot-password-button'
            >
              Forgot Password?
            </Button>
          </Box>
          <FccDisclaimer />
        </Box>
      </form>
    </Box>
  );
};

export default LoginForm;
